import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Input } from '@mui/material';
import { CommonDatagrid } from '..';
import { getCustomerAppProductColumns } from './columns';
import { useNavigate } from 'react-router-dom';
import { CUSTOMER_APP_ROUTE } from '../../../constants/routes';
import { IRootState } from '../../../types/store';
import { useDispatch, useSelector } from 'react-redux';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { CustomerAppService } from '../../../api/services/customerAppService';
import { clearProducts, setDisplayedProducts } from '../../../store/Reducers/CustomerAppSlice';
import { IProduct } from '../../../types/product';
import { FlashdealScheduleCard } from '../../FlashdealScheduler';
import { ICustomerDatagridProps } from '../../../types/commonDatagrid';
import { RefreshButton } from '../../Buttons/RefreshButton';

export const CustomerAppDatagrid = ({ section, headerLabel = '' }: ICustomerDatagridProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { content: data } = useSelector((state: IRootState) => state.customerApp);

  const [skuInput, setSkuInput] = useState('');

  const columns = useMemo(() => {
    return getCustomerAppProductColumns(section);
  }, [section, data]);

  const retrieveData = async () => {
    try {
      const flashedProducts = await CustomerAppService.getAllProductsBySection(section);
      dispatch(setDisplayedProducts(flashedProducts));
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve products. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const addProductToList = async () => {
    if (!skuInput) {
      dispatch(
        showNotification({
          message: 'Please enter a SKU.',
          severity: 'warning',
        }),
      );
      return;
    }

    try {
      const currentList = data ?? [];

      const newList = currentList.map((x: IProduct, index: number) => ({
        sequence: index + 1,
        sku: x.sKU,
      }));

      newList.push({
        sequence: newList.length + 1,
        sku: skuInput.trim(),
      });

      const payload = {
        items: newList,
      };

      const updatedFlashdeals = await CustomerAppService.updateProductsBySection(section, payload);

      dispatch(setDisplayedProducts(updatedFlashdeals));

      dispatch(
        showNotification({
          message: `Successfully added ${skuInput} to ${section.toUpperCase()}.`,
        }),
      );

      setSkuInput('');
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to add product to ${section.toUpperCase()}. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const shuffleProducts = async () => {
    try {
      const currentList = [...(data ?? [])];

      // Fisher-Yates shuffle
      for (let i = currentList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [currentList[i], currentList[j]] = [currentList[j], currentList[i]];
      }

      const payload = {
        items: currentList.map((x: IProduct, index: number) => ({
          sequence: index + 1,
          sku: x.sKU,
        })),
      };

      const updatedProducts = await CustomerAppService.updateProductsBySection(section, payload);

      dispatch(setDisplayedProducts(updatedProducts));

      dispatch(
        showNotification({
          message: 'Product sequence shuffled successfully.',
        }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to shuffle products. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(clearProducts());
    retrieveData();
  }, [section]);

  return (
    <>
      <CommonDatagrid
        key={section}
        header={
          <Box>
            <p>{headerLabel}</p>
            {section == 'flashdeals' ? <FlashdealScheduleCard /> : <></>}
          </Box>
        }
        data={data}
        columns={columns}
        rowId={'iD'}
        headerActionButtons={
          <Box
            gap={2}
            display={'flex'}
            flexDirection={'row'}
            alignItems="flex-start"
            justifyContent={'space-between'}
          >
            <Box gap={2} display={'flex'} flexDirection={'row'}>
              <RefreshButton
                onClick={() => {
                  retrieveData();
                }}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 10,
                }}
              >
                <Input
                  placeholder="SK-00000"
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    px: 1,
                  }}
                />

                <Button
                  style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
                  variant="contained"
                  onClick={addProductToList}
                >
                  Add Product
                </Button>
              </div>

              <Button
                style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
                variant="contained"
                color="warning"
                onClick={() => {
                  shuffleProducts();
                }}
              >
                Shuffle
              </Button>
              <Button
                style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
                variant="contained"
                onClick={() => {
                  navigate(`${CUSTOMER_APP_ROUTE}/${section}/reordersequence`, {
                    state: data,
                  });
                }}
              >
                Reorder Sequence
              </Button>
            </Box>
          </Box>
        }
        searchbarPlaceholder={'Search product by SKU...'}
        searchKey="name"
      />
    </>
  );
};
